import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import {getTopicDataLV} from "../../../api/topicDataAPI";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { useTranslation } from "react-i18next";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";
import { formatDateTime } from "../../../utils/dateUtils";

export default function LastValuesDataGrid() {
  const { t } = useTranslation();
  const location = useLocation();
  const isPageVisible = usePageVisibility();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();
  const [groupedData, setGroupedData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const columns = useMemo(
    () => [
      {
        field: "objectName",
        headerName: t("lastValues.objectName"),
        flex: 1,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "values",
        headerName: t("lastValues.values"),
        flex: 1,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      // {
      //   field: "updateDate",
      //   headerName: t("lastValues.updateDate"),
      //   flex: 2,
      //   minWidth: 120,
      //   align: "center",
      //   headerAlign: "center",
      // },
    ],
    [t]
  );

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? rawId : null;
  const { interval, isRunning } = useRefreshInterval();
  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  useEffect(() => {
    if (!isRunning) {
      // When stopped, don't fetch new data but keep existing data
      return;
    }

    async function fetchStatus() {
      // Sayfa görünür değilse veri çekme
      if (!isPageVisible) return;

      try {
        const result = await getTopicDataLV(node_id);
        if (!result || (Array.isArray(result) && result.length === 0)) {
          setGroupedData([]);
          setLastUpdated(null);
          return;
        }
        const data = Array.isArray(result) ? result[0] : result;
        if (!data || data.epoch == null) {
          setGroupedData([]);
          setLastUpdated(null);
          return;
        }
        const formattedEpoch = formatDateTime(new Date(data.epoch * 1000));
        setLastUpdated(formattedEpoch);
        const rows = Object.entries(data)
          .filter(([key]) => /^\d+$/.test(key))
          .map(([key, value]) => {
            const idNum = parseInt(key, 10);
            const matched = objectData.find((obj) => obj.idObject === idNum);
            let objectName = key;
            let convertedValue = value;
            if (matched) {
              objectName = matched.name;
              convertedValue = ConvertOIDValueToString(
                { Type: matched.type },
                value
              );
            }
            return {
              id: key,
              objectName,
              values: convertedValue,
              updateDate: formattedEpoch,
            };
          });
        setGroupedData(rows);
      } catch (error) {
        console.error("Error fetching last values:", error);
      }
    }

    // Initial fetch
    fetchStatus();

    // Set up interval only if running
    const intervalId = setInterval(fetchStatus, intervalMs);

    return () => clearInterval(intervalId);
  }, [node_id, intervalMs, isRunning, isPageVisible, objectData]);
  return (
    <Box
      sx={{
        height: { xs: 400, sm: 400, md: "100%" },
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        justifyContent="space-around"
        marginBottom={2}
      >
        <Typography
          variant="h2"
          align="center"
          sx={{
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: "1.75rem", sm: "2rem" },
          }}
        >
          {t("summary.latestValues")}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            ml: { sm: 2 },
            px: 2,
            py: 1,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Stack
            direction="column"
            spacing={0}
            sx={{ alignItems: { xs: "center" } }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("lastValues.updateDate")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lastUpdated ?? "-"}
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <DataGrid
        rows={groupedData.length > 0 ? groupedData : []}
        columns={columns}
        hideFooter={true}
        disableRowSelectionOnClick
        density="compact"
        autoHeight
        sx={{
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            lineHeight: "normal",
            padding: "8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.paper",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-main": {
            width: "100%",
            overflow: "auto",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto",
          },
        }}
        localeText={{ noRowsLabel: t("lastValues.noValues") }}
      />
    </Box>
  );
}
